import React from 'react';
import ReactDOM from 'react-dom';
import Icon from '@mdi/react';
import { mdiRocket } from '@mdi/js';
import { ArrowTrendingUpIcon, BriefcaseIcon, IdentificationIcon, UserGroupIcon, Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { injectIntl, IntlProvider } from 'react-intl';
import translations from '../../lib/i18n/translations.json';
import { categoriesData } from '../Home/categoriesData';
import GeorgeIcon from '../icons/GeorgeIcon.jsx';
import PolicyModalContainer from '../../containers/Succession/PolicyModalContainer';
import { POLICIES, legalLinksEnabled, trackLegalLinkClick } from './Legal/legalLinks';

const MOBILE_BREAKPOINT = 1024;

const isActiveRoute = (currentPath, route) => {
  if (!currentPath || !route) return false;
  return currentPath === route || currentPath.startsWith(`${route}/`);
};

// MDI Rocket icon component
const RocketIcon = ({ className }) => (
  <Icon path={mdiRocket} size={1.25} className={className} />
);

const hasFeature = (features = [], featureName) => {
  if (!Array.isArray(features)) return false;
  return features.some((f) => String(f) === featureName || String(f) === `${featureName}_type`);
};

const NavItem = ({ href, label, svgPath, icon: IconComponent, active, onClick, hasSubmenu, onMouseEnter, onMouseLeave }) => (
  <a
    href={href}
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    className={`group flex flex-col items-center justify-center p-2 rounded-lg text-xs font-medium transition-colors min-h-[56px] relative ${active ? 'bg-succession-primary/10 text-succession-primary' : 'text-succession-text-secondary hover:bg-succession-bg-secondary hover:text-succession-primary'}`}
    aria-current={active ? 'page' : undefined}
    aria-haspopup={hasSubmenu ? 'true' : undefined}
  >
    {IconComponent ? (
      <IconComponent className={`w-5 h-5 mb-1 ${active ? 'text-succession-primary' : 'text-succession-text-muted group-hover:text-succession-primary'}`} />
    ) : (
      <svg className={`w-5 h-5 mb-1 ${active ? 'text-succession-primary' : 'text-succession-text-muted group-hover:text-succession-primary'}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={svgPath} />
      </svg>
    )}
    <span className="text-center leading-tight">{label}</span>
  </a>
);

class SidebarNavigation extends React.Component {
  constructor(props) {
    super(props);
    const isNarrow = typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT;
    this.state = { openCat: null, panelPos: { top: 80, left: 96 }, mobileOpen: false, isNarrow, openPolicy: null };
    this.onDocClick = this.onDocClick.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onViewportResize = this.onViewportResize.bind(this);
    this.toggleMobile = this.toggleMobile.bind(this);
    this.closeMobile = this.closeMobile.bind(this);
    this.closePolicy = this.closePolicy.bind(this);
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.onDocClick);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('resize', this.onViewportResize);
    window.addEventListener('scroll', this.onResize, true);
    this.computePanelPos();
    // Re-check on mount (constructor may have run during SSR with no window)
    const isNarrow = window.innerWidth < MOBILE_BREAKPOINT;
    if (isNarrow !== this.state.isNarrow) this.setState({ isNarrow });
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.onDocClick);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('resize', this.onViewportResize);
    window.removeEventListener('scroll', this.onResize, true);
    if (this.hoverTimeout) clearTimeout(this.hoverTimeout);
    this.applySidebarOpen(false);
  }

  componentDidUpdate(_prevProps, prevState) {
    if (prevState.mobileOpen !== this.state.mobileOpen) {
      this.applySidebarOpen(this.state.mobileOpen);
    }
  }

  applySidebarOpen(open) {
    if (typeof document === 'undefined') return;
    // Toggle only the existing CSS hook on #sidebar; render our own backdrop in renderMobileToggle
    // so we don't fight the legacy #mobile-menu-overlay (whose onclick targets the legacy global).
    const sidebar = document.getElementById('sidebar');
    if (sidebar) sidebar.classList.toggle('mobile-open', open);
    document.body.classList.toggle('mobile-menu-open', open);
  }

  onViewportResize() {
    const isNarrow = window.innerWidth < MOBILE_BREAKPOINT;
    if (isNarrow !== this.state.isNarrow) {
      this.setState({ isNarrow, mobileOpen: isNarrow ? this.state.mobileOpen : false });
    } else if (!isNarrow && this.state.mobileOpen) {
      this.setState({ mobileOpen: false });
    }
  }

  toggleMobile() {
    this.setState((s) => ({ mobileOpen: !s.mobileOpen, openCat: null }));
  }

  closeMobile() {
    if (this.state.mobileOpen) this.setState({ mobileOpen: false });
  }

  handlePolicyClick = (policy) => (e) => {
    e.preventDefault();
    trackLegalLinkClick(policy, 'sidebar');
    this.setState({ openPolicy: policy, mobileOpen: false, openCat: null });
  };

  closePolicy() {
    this.setState({ openPolicy: null });
  }

  renderLegalLinks() {
    const { intl } = this.props;
    const links = [
      { key: 'privacy', labelId: 'succession.sidebar.privacy', defaultMessage: 'Privacy' },
      { key: 'terms', labelId: 'succession.sidebar.terms', defaultMessage: 'Terms' },
    ];
    return (
      <div className="flex flex-col items-center gap-1 mb-2">
        {links.map(({ key, labelId, defaultMessage }) => (
          <a
            key={key}
            href={POLICIES[key].href}
            onClick={this.handlePolicyClick(key)}
            className="text-xs text-center text-succession-text-muted hover:text-succession-primary transition-colors"
          >
            {intl.formatMessage({ id: labelId, defaultMessage })}
          </a>
        ))}
      </div>
    );
  }

  onDocClick(e) {
    const panel = document.querySelector('[data-sidebar-tools-panel="1"]');
    const sidebar = document.getElementById('sidebar');
    if (panel && !panel.contains(e.target) && sidebar && !sidebar.contains(e.target)) {
      this.setState({ openCat: null });
    }
  }

  onResize() {
    if (this.state.openCat) this.computePanelPos();
  }

  computePanelPos() {
    try {
      const sidebar = document.getElementById('sidebar');
      const rect = sidebar ? sidebar.getBoundingClientRect() : { right: 96, top: 64 };
      const top = Math.max(64, Math.round(rect.top + 16));
      const left = Math.round(rect.right + 12);
      this.setState({ panelPos: { top, left } });
    } catch (_) {}
  }

  // Keys that have submenus (hover to reveal)
  submenuKeys = ['succession', 'planning', 'growth', 'value_building'];
  // Keys that should NOT navigate on click (only toggle submenu)
  nonNavigableKeys = ['planning'];
  // Categories gated by the disable_category_landing_pages feature flag
  categoryLandingKeys = ['succession', 'growth', 'value_building'];

  handleCategoryClick = (key) => (e) => {
    const { features = [] } = this.props;
    const categoryLandingDisabled = hasFeature(features, 'disable_category_landing_pages');

    // When the flag is on, treat succession/growth/value_building like 'planning' — toggle submenu only
    const isNonNavigable = this.nonNavigableKeys.includes(key)
      || (categoryLandingDisabled && this.categoryLandingKeys.includes(key));

    if (isNonNavigable) {
      e.preventDefault();
      this.computePanelPos();
      this.setState((s) => ({ openCat: s.openCat === key ? null : key }));
      return;
    }
    // For succession, growth, and value_building: allow navigation (don't prevent default), but close submenu
    if (this.categoryLandingKeys.includes(key)) {
      this.setState({ openCat: null });
    }
    // Close the mobile drawer on actual navigation (any leaf nav click while mobile-open)
    this.closeMobile();
  };

  handleCategoryHover = (key) => () => {
    // Open submenu on hover for items with submenus
    if (this.submenuKeys.includes(key)) {
      if (this.hoverTimeout) clearTimeout(this.hoverTimeout);
      this.computePanelPos();
      this.setState({ openCat: key });
    }
  };

  handleCategoryLeave = (key) => () => {
    // Delay closing to allow moving to the panel
    if (this.submenuKeys.includes(key)) {
      this.hoverTimeout = setTimeout(() => {
        if (this.state.openCat === key) {
          this.setState({ openCat: null });
        }
      }, 150);
    }
  };

  handlePanelEnter = () => {
    // Cancel close timeout when entering panel
    if (this.hoverTimeout) clearTimeout(this.hoverTimeout);
  };

  handlePanelLeave = () => {
    // Close panel when leaving
    this.hoverTimeout = setTimeout(() => {
      this.setState({ openCat: null });
    }, 150);
  };

  keyToCategoryId = (key) => {
    if (key === 'planning' || key === 'growth') return 'opportunity';
    if (key === 'value_building') return 'growth'; // Use growth category as base
    return key;
  };

  // Filter tools for specific submenus
  getFilteredTools = (key, tools, featureFlags = {}) => {
    const { dealRoomEnabled, partialSalesEnabled, teamingEnabled, cfaCalculatorEnabled, multiLineValuationEnabled, customValuationEnabled, studentConnectionPlatformEnabled, loanRepaymentCalculatorEnabled, dealRoomBrandedName } = featureFlags;
    const valuationEnabled = multiLineValuationEnabled || customValuationEnabled;

    if (key === 'succession') {
      // Filter succession tools based on feature flags
      const filtered = tools.filter(tool => {
        if (tool.id === 'deals' && !dealRoomEnabled) return false;
        if (tool.id === 'ps' && !partialSalesEnabled) return false;
        if (tool.id === 'valuation' && !valuationEnabled) return false;
        if (tool.id === 'planning' && !featureFlags.planBuilderEnabled) return false;
        return true;
      });
      
      return filtered.map((tool) => {
        if (tool.id === 'valuation') {
          return { ...tool, route: multiLineValuationEnabled ? '/succession/valuation/multi' : '/succession/valuation' };
        }
        if (tool.id === 'deals' && dealRoomBrandedName) {
          return { ...tool, label: dealRoomBrandedName, labelKey: undefined };
        }
        return tool;
      });
    }
    if (key === 'growth') {
      // Show Discovery, Cash Flow Analysis (if enabled), My Rolodex, Teaming (if enabled), Financing for Growth submenu
      const opportunityToolIds = ['discovery', 'rolodex', ...(teamingEnabled ? ['teaming'] : []), ...(loanRepaymentCalculatorEnabled ? ['financing'] : [])];
      const filteredTools = tools.filter(tool => opportunityToolIds.includes(tool.id));
      
      // Add Cash Flow Analysis from the 'growth' category (after Discovery) - only if feature enabled
      if (cfaCalculatorEnabled) {
        const growthCategory = categoriesData.find(c => c.id === 'growth');
        const cfaTool = growthCategory?.tools?.find(t => t.id === 'cfa');
        if (cfaTool) {
          // Insert after Discovery (index 0)
          const discoveryIndex = filteredTools.findIndex(t => t.id === 'discovery');
          if (discoveryIndex !== -1) {
            filteredTools.splice(discoveryIndex + 1, 0, cfaTool);
          } else {
            filteredTools.unshift(cfaTool);
          }
        }
      }
      
      // Add Hiring tool if student_connection_platform feature is enabled
      if (studentConnectionPlatformEnabled) {
        filteredTools.push({ id: 'hiring', label: 'Recruitment', labelKey: 'succession.tools.hiring', route: '/hiring', icon: BriefcaseIcon });
      }
      
      return filteredTools;
    }
    if (key === 'value_building') {
      // Custom tools for Value Building submenu
      const growthCategory = categoriesData.find(c => c.id === 'growth');
      const sprintTool = growthCategory?.tools?.find(t => t.id === 'sprint');
      const mapTool = growthCategory?.tools?.find(t => t.id === 'map');
      
      // Build custom tools array: Value Building, Sprint, Master Action Plan
      const valueBuildingTools = [
        { id: 'value_building_home', label: 'Value Building', labelKey: 'succession.tools.value_building_home', route: '/succession/value_acceleration', icon: ArrowTrendingUpIcon },
      ];
      if (sprintTool) valueBuildingTools.push(sprintTool);
      if (mapTool) valueBuildingTools.push(mapTool);
      
      return valueBuildingTools;
    }
    return tools;
  };

  renderMobileToggle() {
    if (typeof document === 'undefined') return null;
    const { mobileOpen, isNarrow } = this.state;
    if (!isNarrow) return null;

    const ToggleIcon = mobileOpen ? XMarkIcon : Bars3Icon;
    const buttonStyle = {
      position: 'fixed',
      top: '12px',
      left: '12px',
      width: '44px',
      height: '44px',
      zIndex: 1060,
    };
    const overlayStyle = {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1040,
    };
    const node = (
      <>
        <button
          type="button"
          onClick={this.toggleMobile}
          aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
          aria-expanded={mobileOpen}
          aria-controls="sidebar"
          style={buttonStyle}
          className="inline-flex items-center justify-center rounded-lg bg-succession-bg-primary border border-succession-border text-succession-text-secondary hover:text-succession-primary hover:bg-succession-bg-secondary shadow-succession-md transition-colors"
        >
          <ToggleIcon className="w-6 h-6" />
        </button>
        {mobileOpen && (
          <div
            onClick={this.closeMobile}
            style={overlayStyle}
            aria-hidden="true"
          />
        )}
      </>
    );
    return ReactDOM.createPortal(node, document.body);
  }

  renderToolsPanel() {
    const { openCat, panelPos } = this.state;
    const {
      dealRoomEnabled = false,
      partialSalesEnabled = false,
      teamingEnabled = false,
      cfaCalculatorEnabled = false,
      multiLineValuationEnabled = false,
      customValuationEnabled = false,
      studentConnectionPlatformEnabled = false,
      loanRepaymentCalculatorEnabled: loanRepaymentCalculatorEnabledProp = false,
      features = [],
      dealRoomBrandedName,
      intl,
    } = this.props;
    const partialSalesFeatureEnabled = (Array.isArray(features) && features.length > 0)
      ? hasFeature(features, 'partial_sales')
      : partialSalesEnabled;

    if (!openCat || typeof document === 'undefined') return null;
    const cat = categoriesData.find((c) => c.id === this.keyToCategoryId(openCat));
    if (!cat) return null;

    // Get filtered tools for this submenu
    const loanRepaymentCalculatorEnabled = loanRepaymentCalculatorEnabledProp || hasFeature(features, 'loan_repayment_calculator');
    const planBuilderEnabled = hasFeature(features, 'plan_builder');
    const featureFlags = { dealRoomEnabled, partialSalesEnabled: partialSalesFeatureEnabled, teamingEnabled, cfaCalculatorEnabled, multiLineValuationEnabled, customValuationEnabled, studentConnectionPlatformEnabled, loanRepaymentCalculatorEnabled, planBuilderEnabled, dealRoomBrandedName };
    const tools = this.getFilteredTools(openCat, cat.tools || [], featureFlags);
    
    // Use single column for succession, growth, and value_building, two columns for others
    const isSingleColumn = ['succession', 'growth', 'value_building'].includes(openCat);
    const panelWidth = isSingleColumn ? '220px' : '760px';
    
    // Use the sidebar key label for display (e.g., "Growth" instead of "Opportunities")
    const displayLabels = {
      growth: intl.formatMessage({ id: 'succession.sidebar.growth' }),
      value_building: intl.formatMessage({ id: 'succession.sidebar.value_building' }),
    };
    const displayLabel = displayLabels[openCat] || cat.label;
    
    const panel = (
      <div
        data-sidebar-tools-panel="1"
        className="fixed bg-succession-bg-secondary border border-succession-border rounded-xl shadow-succession-lg p-4 max-h-[70vh] overflow-auto"
        style={{ top: `${panelPos.top}px`, left: `${panelPos.left}px`, width: panelWidth, maxWidth: '92vw', zIndex: 999999 }}
        onMouseEnter={this.handlePanelEnter}
        onMouseLeave={this.handlePanelLeave}
      >
        <div className="text-xs font-semibold mb-3 text-succession-text-muted uppercase tracking-wide">{displayLabel}</div>
        <div className={isSingleColumn ? 'flex flex-col space-y-1' : 'grid grid-cols-1 md:grid-cols-2 gap-6'}>
          {tools.map((tool) => (
            <a
              key={`side-${cat.id}-${tool.id}`}
              href={tool.route || tool.href || '#'}
              className={isSingleColumn 
                ? 'flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-succession-hover-bg text-succession-text-primary hover:text-succession-text-primary text-sm font-medium transition-colors'
                : 'flex items-center gap-2 p-2 rounded-md bg-succession-bg-primary border border-succession-border hover:bg-succession-hover-bg text-succession-text-secondary hover:text-succession-text-primary text-xs'
              }
              onClick={() => this.setState({ openCat: null })}
            >
              {tool.icon ? (<tool.icon className={isSingleColumn ? 'w-5 h-5 text-succession-text-muted' : 'w-4 h-4'} />) : (<span className={isSingleColumn ? 'w-5 h-5' : 'w-4 h-4'} />)}
              <span className={isSingleColumn ? '' : 'font-medium truncate'}>
                {tool.labelKey ? intl.formatMessage({ id: tool.labelKey, defaultMessage: tool.label }) : tool.label}
              </span>
              {tool.comingSoon && <span className="ml-auto text-[10px] text-gray-400">{intl.formatMessage({ id: 'succession.sidebar.soon' })}</span>}
            </a>
          ))}
        </div>
      </div>
    );
    return ReactDOM.createPortal(panel, document.body);
  }

  render() {
    const { 
      paths = {},
      currentPath = '',
      managerViewEnabled = false,
      managerViewPath = '/succession/manager-view',
      vaultEnabled = false,
      sponsoredOwnersEnabled = false,
      marketplaceSprintsEnabled = false, 
      aiAssistantEnabled = false, 
      valueAccelerationEnabled = false,
      dealRoomEnabled = false,
      partialSalesEnabled = false,
      teamingEnabled = false,
      cfaCalculatorEnabled = false,
      multiLineValuationEnabled = false,
      studentConnectionPlatformEnabled = false,
      loanRepaymentCalculatorEnabled: loanRepaymentCalculatorEnabledProp = false,
      features = [],
      georgeCoachEnabled = false,
    } = this.props;
    const partialSalesFeatureEnabled = (Array.isArray(features) && features.length > 0)
      ? hasFeature(features, 'partial_sales')
      : partialSalesEnabled;
    const loanRepaymentCalculatorEnabled = loanRepaymentCalculatorEnabledProp || hasFeature(features, 'loan_repayment_calculator');
    const planBuilderEnabled = hasFeature(features, 'plan_builder');
    const customValuationEnabled = hasFeature(features, 'custom_valuation');
    const valuationEnabled = multiLineValuationEnabled || customValuationEnabled;
    const simpleMarketplaceApprovalEnabled = hasFeature(features, 'simple_marketplace_approval');
    const legalEnabled = legalLinksEnabled(features);
    const { openPolicy } = this.state;

    // Succession submenu is empty unless at least one of its tools is enabled.
    // Hide the top-level Succession nav entry when there's nothing to reveal.
    const successionHasTools = dealRoomEnabled || partialSalesFeatureEnabled || valuationEnabled || planBuilderEnabled;

    const { intl } = this.props;
    const items = [
      { key: 'home', label: intl.formatMessage({ id: 'succession.sidebar.home', defaultMessage: 'Home' }), href: paths.home, svgPath: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
      // Succession opens a submenu on hover AND navigates to succession page on click
      ...(successionHasTools ? [{ key: 'succession', label: intl.formatMessage({ id: 'succession.sidebar.succession', defaultMessage: 'Succession' }), href: '/succession/succession', svgPath: 'M12 22s8-4 8-10V7l-8-5-8 5v5c0 6 8 10 8 10z', hasSubmenu: true }] : []),
      // Growth submenu — hidden when simple_marketplace_approval is on (Listings + Rolodex
      // top-level items cover the same territory, so the Growth submenu is redundant).
      ...(simpleMarketplaceApprovalEnabled ? [] : [
        { key: 'growth', label: intl.formatMessage({ id: 'succession.sidebar.growth', defaultMessage: 'Growth' }), href: '/succession/growth', svgPath: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z', hasSubmenu: true },
      ]),
      // Marketplace approval light-weight mode: surface Listings + Rolodex as top-level items
      ...(simpleMarketplaceApprovalEnabled ? [
        { key: 'listings', label: intl.formatMessage({ id: 'succession.sidebar.listings', defaultMessage: 'Listings' }), href: '/succession/search', icon: IdentificationIcon },
        { key: 'rolodex', label: intl.formatMessage({ id: 'succession.sidebar.rolodex', defaultMessage: 'Rolodex' }), href: '/succession/rolodex', icon: UserGroupIcon },
      ] : []),
      // Value Building - only shown if feature enabled, has submenu on hover AND navigates on click
      ...(valueAccelerationEnabled ? [{ key: 'value_building', label: intl.formatMessage({ id: 'succession.sidebar.value_building', defaultMessage: 'Value Building' }), href: '/succession/value_acceleration', svgPath: 'M3 17l6-6 4 4 8-8', hasSubmenu: true }] : []),
      { key: 'learn', label: intl.formatMessage({ id: 'succession.sidebar.learn', defaultMessage: 'Learn' }), href: '/succession/education', svgPath: 'M4 6l8-3 8 3-8 3-8-3zm0 6l8 3 8-3' },
      // Join a Sprint (rocketship icon) - directly below Learn
      ...(marketplaceSprintsEnabled ? [{ key: 'join_sprint', label: intl.formatMessage({ id: 'succession.sidebar.join_sprint', defaultMessage: 'Join a Sprint' }), href: '/sprints/marketplace', icon: RocketIcon }] : []),
      // Assistant (sparkle icon) - moved below Join a Sprint, only shown if feature enabled
      ...(aiAssistantEnabled ? [{ key: 'assistant', label: georgeCoachEnabled ? intl.formatMessage({ id: 'succession.sidebar.ask_george', defaultMessage: 'Ask George' }) : intl.formatMessage({ id: 'succession.sidebar.assistant', defaultMessage: 'Assistant' }), href: '/succession/assistant', ...(georgeCoachEnabled ? { icon: GeorgeIcon } : { svgPath: 'M11 3l1.5 3.5L16 8l-3.5 1.5L11 13l-1.5-3.5L6 8l3.5-1.5L11 3z M18 4l.8 1.9L21 7l-2.2 1.1L18 10l-.8-1.9L15 7l2.2-1.1L18 4z M5 13l.8 1.9L8 16l-2.2 1.1L5 19l-.8-1.9L2 16l2.2-1.1L5 13z' }) }] : []),
      // Succeedo Phase 5: owners this advisor sponsors on Succeedo
      ...(sponsoredOwnersEnabled ? [{ key: 'sponsored_owners', label: intl.formatMessage({ id: 'succession.sidebar.sponsored_owners', defaultMessage: 'Sponsored owners' }), href: '/succession/sponsored-owners', icon: BriefcaseIcon }] : []),
      ...(vaultEnabled ? [{ key: 'vault', label: intl.formatMessage({ id: 'succession.sidebar.vault', defaultMessage: 'Vault' }), href: '/succession/vault', svgPath: 'M4 4h16v14H4V4zm2 2v10h12V6H6zm6 2a2 2 0 012 2v2a2 2 0 01-2 2 2 2 0 01-2-2v-2a2 2 0 012-2z' }] : []),
      // Manager View — only for approvers with advisors reporting to them
      ...(managerViewEnabled ? [{ key: 'manager_view', label: intl.formatMessage({ id: 'succession.sidebar.manager_view', defaultMessage: 'Manager View' }), href: managerViewPath, icon: UserGroupIcon }] : []),
      { key: 'messages', label: intl.formatMessage({ id: 'succession.sidebar.messages', defaultMessage: 'Messages' }), href: paths.messages, svgPath: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' },
    ];

    return (
      <div className="flex flex-col h-full w-20 shrink-0" role="navigation" aria-label="Primary" style={{ contain: 'layout paint' }}>
        <nav className="flex-1 p-2 pt-16 lg:pt-4">
          <div className="space-y-2">
            {items.map((item) => {
              // For items with submenus, check if any of the submenu routes are active
              let isActive = isActiveRoute(currentPath, item.href);
              if (item.key === 'succession') {
                const successionRoutes = [
                  '/succession/succession',
                  '/succession/valuation',
                  ...(multiLineValuationEnabled ? ['/succession/valuation/multi'] : []),
                  '/succession/plans',
                  ...(dealRoomEnabled ? ['/succession/deals'] : []),
                  ...(partialSalesFeatureEnabled ? ['/succession/partial_sales'] : []),
                ];
                isActive = this.state.openCat === 'succession' || successionRoutes.some(route => isActiveRoute(currentPath, route));
              }
              if (item.key === 'growth') {
                const growthRoutes = [
                  '/succession/growth',
                  // When simple_marketplace_approval is on, Listings/Rolodex are top-level
                  // items — avoid double-highlighting by scoping them out of Growth's active set.
                  ...(simpleMarketplaceApprovalEnabled ? [] : ['/succession/search', '/succession/rolodex']),
                  ...(loanRepaymentCalculatorEnabled ? ['/succession/financing'] : []),
                  ...(cfaCalculatorEnabled ? ['/succession/calculators/cfa'] : []),
                  ...(studentConnectionPlatformEnabled ? ['/hiring'] : []),
                ];
                isActive = this.state.openCat === 'growth' || growthRoutes.some(route => isActiveRoute(currentPath, route));
              }
              if (item.key === 'value_building') {
                const valueBuildingRoutes = ['/succession/value_acceleration', '/sprints', '/succession/advisor-map'];
                isActive = this.state.openCat === 'value_building' || valueBuildingRoutes.some(route => isActiveRoute(currentPath, route));
              }
              return (
                <NavItem
                  key={item.key}
                  href={item.href}
                  label={item.label}
                  svgPath={item.svgPath}
                  icon={item.icon}
                  active={isActive}
                  onClick={this.handleCategoryClick(item.key)}
                  hasSubmenu={item.hasSubmenu}
                  onMouseEnter={item.hasSubmenu ? this.handleCategoryHover(item.key) : undefined}
                  onMouseLeave={item.hasSubmenu ? this.handleCategoryLeave(item.key) : undefined}
                />
              );
            })}
          </div>
        </nav>
        <div className="p-2 border-t border-succession-border">
          {legalEnabled && this.renderLegalLinks()}
          <div className="text-xs text-succession-text-muted text-center">v2.0</div>
        </div>
        {legalEnabled && <PolicyModalContainer policy={openPolicy} onClose={this.closePolicy} />}
        {this.renderToolsPanel()}
        {this.renderMobileToggle()}
      </div>
    );
  }
}

const SidebarNavigationWithIntl = (props, railsContext) => {
  window.locale = (railsContext && railsContext.i18nLocale) || window.locale || 'en';
  const locale = window.locale;
  return (
    <IntlProvider locale={locale} messages={translations[locale] || translations.en}>
      <SidebarNavigationInjected {...props} />
    </IntlProvider>
  );
};

const SidebarNavigationInjected = injectIntl(SidebarNavigation);

export default SidebarNavigationWithIntl;


